// TextRichField — the REAL Froala rich-text body for the v2 Text editor (WL-011d).
//
// PARITY BLOCKER CLOSE: WL-011 shipped the v2 TextEditor with a plain <textarea>
// body + a fake formatting toolbar, so the new Text editor LOST rich-text
// authoring and the legacy Text block could not be deleted. This mounts the SAME
// `FroalaTextEditor` component the legacy Text block uses (src/components/
// SegmentBuilder/CodeBlocks/Text/Form/index.jsx -> FroalaTextEditor), with the
// SAME props and therefore the SAME toolbar/allowed-tags/paste config, so the HTML
// it writes to the craft node's `text` prop is BYTE-IDENTICAL to the legacy editor.
// A wheel authored in v1 renders identically after a v2 edit, and the stored HTML
// stays compatible with sanitizeUserHtml (the flag-gated DOMPurify allow-list) —
// because it is the exact same Froala output the sanitizer was tuned for.
//
// DEDUP NOTE (for the FE dev): the repo carries two rich-text engines (Froala +
// the src/components/Editor RichTextEditor). This PORT deliberately reuses Froala
// to guarantee parity FIRST; consolidating onto one engine is a separate, opt-in
// decision — do NOT swap engines here (see the PR description).
//
// The AI Assistant toolbar button is wired to the SAME NewAIPrompt portal the
// legacy TextForm uses; the generation call itself is the app's existing prompt
// UI (no new/paid AI call is triggered from this component).

import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import FroalaTextEditor from "../../../FroalaEditor/children/FroalaTextEditor";
import NewAIPrompt from "../../../NewAiPrompt";
import { handleAiAssistantIcon } from "../../../FroalaEditor/children/FroalaHelpers";
import { charLimitInASegment } from "../../../SegmentSettings";
import { getSelectedSegmentCharCount } from "../../../../redux/selectors/segmentBuilderSelectors";

// Mirrors the legacy TextForm body wiring 1:1 so the emitted HTML is byte-identical.
const TextRichField = ({
  value,
  onChange,
  conversationKey = null,
  headingProps = null,
  paragraphProps = null,
  lineHeight = null,
  placeholder = "Add Text Here",
}) => {
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const editorRef = useRef(null);

  const selectedSegmentCharCount = useSelector(getSelectedSegmentCharCount);

  const handleFroalaCustomization = (FroalaEditor) => {
    handleAiAssistantIcon(FroalaEditor, setShowAiPrompt);
  };

  return (
    <>
      <FroalaTextEditor
        name={`text_body`}
        id={1}
        value={value}
        onChange={onChange}
        handleEditorCustomization={handleFroalaCustomization}
        editorRef={editorRef}
        placeholder={placeholder}
        label={"Text Field"}
        lineHeight={lineHeight}
        headingProps={headingProps}
        paragraphProps={paragraphProps}
        disableAtCharLimit
      />

      {/* Segment-level character-count indicator — identical to legacy TextForm. */}
      <div
        style={{
          textAlign: "right",
          fontSize: "12px",
          color:
            selectedSegmentCharCount > charLimitInASegment ? "#ff4d4f" : "#999",
          margin: "5px 16px 10px 16px",
        }}
      >
        {selectedSegmentCharCount > charLimitInASegment
          ? `${charLimitInASegment}/${charLimitInASegment} (Segment character limit exceeded by ${
              selectedSegmentCharCount - charLimitInASegment
            } characters)`
          : `${selectedSegmentCharCount}/${charLimitInASegment}`}
      </div>

      {showAiPrompt &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              zIndex: 6522299,
              right: "35vw",
              top: "25vh",
              overflow: "auto",
            }}
          >
            <NewAIPrompt
              modalWidth="350px"
              conversationKey={conversationKey}
              onCloseIconPress={() => setShowAiPrompt(false)}
            />
          </div>,
          document.body
        )}
    </>
  );
};

export default TextRichField;
